import { Col, Row } from "antd";
import { useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import AppContext from "../../../context/app/context";
import ClientContext from "../../../context/clients/client/context";
import ClientContextProvider from "../../../context/clients/client/provider";
import { ICity } from "../../../models/city/response";
import { IUpdateClient } from "../../../models/client/request";
import FieldBuilder from "../../form-components/field-builder";
import MainForm from "../../form-components/main-form";
import Spin from "../../general/antd/spin";
import { useState } from "react";
import EndPoints from "../../../services/end-points";
import { getAreaStatus } from "../../../models/area/enum";
import { languages } from "../../../utils/helpers/constants";
import FormItem from "../../general/form-item";
import Controller from "../../form-components/controller";
import ImageUploader from "../../form-components/image-uploader";
import { MediumFor } from "../../../models/medium/enum";
import OldImage from "../../form-components/old-image";
import { getTranslations } from "../../../utils/helpers/translations";
import { getBloodType, getGender } from "../../../models/client/enum";

const Form = () => {
  const { t } = useTranslation();
  const { details, actions, loading } = useContext(ClientContext);
  const { id } = useParams();
  const workerStatus = [1, 2, 3];
  const { direction } = useContext(AppContext);
  const [cities, setCities] = useState<ICity[]>([]);
  const [citiesLoading, setCitiesLoading] = useState(true);
  const status = [1, 2];
  const genders = [1, 2];
  const bloodTypes = [1, 2, 3, 4, 5, 6, 7, 8];

  useEffect(() => {
    const getDetails = async () => {
      id && (await actions.getDetails(Number(id)));
    };
    getDetails();
  }, []);


  useEffect(() => {
    const getCities = async () => {
      try {
        const { data } = await EndPoints.city.getAllCities({
          light: true,
          perPage: -1,
        });
        setCities(data.data);
        setCitiesLoading(false);
      } catch (error) {
      } finally {
        setCitiesLoading(false);
      }
    };
    getCities();
  }, [direction]);


  return (
    <MainForm
      onSubmit={async (data) => {
        details
          ? await actions.updateClient(details?.id, data)
          : await actions.createClient(data);
      }}
      title={details ? t("update_client") : t("add_client")}
      defaultValues={
        {
          birthDate: details?.birthDate,
          cityId: details?.city?.id,
          firstName: details?.user?.firstName,
          imageId: details?.user?.image?.id ?? undefined,
          lastName: details?.user?.lastName,
          mobile: details?.user?.cleanMobile,
          note: getTranslations(details?.user?.translations?.note),
          phone: details?.user?.phone,
          status: details?.user?.status,
          gender: details?.user?.gender,
          bloodType: details?.bloodType,
          nationalIdentityNumber: details?.nationalIdentityNumber,
          healthStatus: getTranslations(details?.translations?.healthStatus),
          height: details?.height,
          weight: details?.weight,
          email: details?.user?.email,
          address: details?.address,
          diseaseId: details?.diseases?.map((dies) => dies?.id),
        } as IUpdateClient
      }
    >
      {loading.includes("details") ? (
        <div style={{ height: "50vh" }}>
          <Spin />
        </div>
      ) : (
        <>
          <Row gutter={[16, 8]}>
            <Col xs={12} sm={12} md={8} lg={8} xl={8} xxl={8}>
              <FieldBuilder
                name="firstName"
                label={t("first_name")}
                input={{ type: "text" }}
                rules={{ required: false }}
              />
            </Col>
            <Col xs={12} sm={12} md={8} lg={8} xl={8} xxl={8}>
              <FieldBuilder
                name="lastName"
                label={t("last_name")}
                input={{ type: "text" }}
                rules={{ required: false }}
              />
            </Col>
            <Col xs={12} sm={12} md={8} lg={8} xl={8} xxl={8}>
              <FieldBuilder
                name="weight"
                label={t("weight")}
                input={{ type: "number" }}
                rules={{ required: false }}
              />
            </Col>
            <Col xs={12} sm={12} md={8} lg={8} xl={8} xxl={8}>
              <FieldBuilder
                name="height"
                label={t("height")}
                input={{ type: "number" }}
                rules={{ required: false }}
              />
            </Col>
            <Col xs={12} sm={12} md={8} lg={8} xl={8} xxl={8}>
              <FieldBuilder
                name="birthDate"
                label={`${t("birth_date")}`}
                input={{ type: "date-picker", format: "YYYY-MM-DD" }}
              />
            </Col>
            <Col xs={12} sm={12} md={8} lg={8} xl={8} xxl={8}>
              <FieldBuilder
                name="mobile"
                label={t("mobile")}
                input={{ type: "text" }}
                rules={{ required: true }}
              />
            </Col>
            <Col xs={12} sm={12} md={8} lg={8} xl={8} xxl={8}>
              <FieldBuilder
                name="cityId"
                label={`${t("city")}`}
                width="large"
                rules={{ required: false }}
                input={{
                  type: "select",
                  loading: citiesLoading,
                  allowSearch: true,
                  options: cities?.map((role, index) => {
                    return {
                      key: index,
                      label: role.name,
                      value: role.id,
                    };
                  }),
                }}
              />
            </Col>
            
            <Col xs={12} sm={12} md={8} lg={8} xl={8} xxl={8}>
              <FieldBuilder
                name="status"
                label={`${t("account_status")}`}
                width="large"
                rules={{ required: false }}
                input={{
                  type: "select",
                  loading: false,
                  options: status?.map((state, index) => {
                    return {
                      key: index,
                      label: t(getAreaStatus(state)),
                      value: state,
                    };
                  }),
                }}
              />
            </Col>
            <Col xs={12} sm={12} md={8} lg={8} xl={8} xxl={8}>
              <FieldBuilder
                name="gender"
                label={`${t("gender")}`}
                width="large"
                rules={{ required: false }}
                input={{
                  type: "select",
                  loading: false,
                  options: genders?.map((gender, index) => {
                    return {
                      key: index,
                      label: t(getGender(gender)),
                      value: gender,
                    };
                  }),
                }}
              />
            </Col>
            <Col xs={12} sm={12} md={8} lg={8} xl={8} xxl={8}>
              <FieldBuilder
                name="bloodType"
                label={`${t("blood_type")}`}
                width="large"
                rules={{ required: false }}
                input={{
                  type: "select",
                  loading: false,
                  options: bloodTypes?.map((type, index) => {
                    return {
                      key: index,
                      label: t(getBloodType(type)),
                      value: t(getBloodType(type)),
                    };
                  }),
                }}
              />
            </Col>
            <Col offset={16}></Col>
            {languages?.map((lang, index) => {
              return (
                <Col span={12}>
                  <FieldBuilder
                    key={index}
                    name={`note[${lang?.code}]`}
                    label={`${t("note")} (${lang?.name})`}
                    input={{ type: "text-area" }}
                    rules={{ required: false }}
                  />
                </Col>
              );
            })}
            <Col span={24}>
              <FormItem label={t("image")}>
                <Controller
                  name="imageId"
                  rules={{
                    required: {
                      value: false,
                      message: `${t("field_is_required_message")}`,
                    },
                  }}
                  render={({ field: { ref, ...field } }) => {
                    return (
                      <ImageUploader
                        {...field}
                        type="DRAGGER"
                        onChange={(response) => {
                          field.onChange(response?.id);
                        }}
                        imageFor={MediumFor.UserProfile}
                      />
                    );
                  }}
                />
              </FormItem>
            </Col>
            {details?.user?.image && (
              <OldImage src={details?.user?.image?.url} />
            )}
          </Row>
        </>
      )}
    </MainForm>
  );
};

const ClientForm = () => {
  return (
    <ClientContextProvider>
      <Form />
    </ClientContextProvider>
  );
};
export default ClientForm;
